import React from 'react';
import { useSearchParams } from 'react-router-dom';

import Filter from '../../../../components/Filter';
import FilterPillGroup from '../../../../components/FilterPanel/FilterPillGroup';
import {
    FilterChip,
    usePublishFilterChips
} from '../../../../components/FilterPanel/FilterChipsContext';
import useGetDeviceStatistics from '../../api/useGetDeviceStatistics';
import useListQueryParams from '../useListQueryParams';

const capitalize = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

const DeviceFilter: React.FC<{ inline?: boolean }> = ({ inline = false }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { deviceStatisticsQuery } = useListQueryParams();
    const { data: deviceStatistics, isLoading: isDeviceStatisticsLoading } =
        useGetDeviceStatistics(deviceStatisticsQuery);

    const handleReset = React.useCallback(() => {
        setSearchParams((prevState) => {
            prevState.delete('online_status');
            prevState.delete('device_status');
            return prevState;
        });
    }, [setSearchParams]);

    const filterLabels = React.useMemo(() => {
        const search = searchParams.get('search');
        const showStatistics = (!search || search?.length === 0) && !isDeviceStatisticsLoading;

        const onlineLabel = showStatistics
            ? `Online (${deviceStatistics?.online_count || 0})`
            : 'Online';

        const offlineLabel = showStatistics
            ? `Offline (${deviceStatistics?.offline_count || 0})`
            : 'Offline';

        const orderedLabel = showStatistics
            ? `Ordered (${deviceStatistics?.ordered_count || 0})`
            : 'Ordered';

        const deliveredLabel = showStatistics
            ? `Delivered (${deviceStatistics?.delivered_count || 0})`
            : 'Delivered';

        const setupNeededLabel = showStatistics
            ? `Setup Needed (${deviceStatistics?.setup_needed_count || 0})`
            : 'Setup Needed';

        return {
            onlineLabel,
            offlineLabel,
            orderedLabel,
            deliveredLabel,
            setupNeededLabel
        };
    }, [searchParams, deviceStatistics, isDeviceStatisticsLoading]);

    const handleFilterSet = React.useCallback(
        (filterName: string, filter: string) => {
            setSearchParams((prevState) => {
                prevState.get(filterName) === filter
                    ? prevState.delete(filterName)
                    : prevState.set(filterName, filter);
                prevState.delete('limit');
                prevState.delete('offset');
                return prevState;
            });
        },
        [setSearchParams]
    );

    const handleOnlineStatusChange = React.useCallback(
        (filter: string) => {
            handleFilterSet('online_status', filter);
        },
        [handleFilterSet]
    );

    const handleDeviceStatusChange = React.useCallback(
        (filter: string) => {
            handleFilterSet('device_status', filter);
        },
        [handleFilterSet]
    );

    const onlineStatus = searchParams.get('online_status');
    const deviceStatus = searchParams.get('device_status');

    const isDisabledStatus = React.useMemo(() => {
        return {
            online: deviceStatus === 'ordered' || deviceStatistics?.online_count === 0,
            offline: deviceStatistics?.offline_count === 0,
            ordered: onlineStatus === 'online' || deviceStatistics?.ordered_count === 0,
            delivered: deviceStatistics?.delivered_count === 0,
            setupNeeded: deviceStatistics?.setup_needed_count === 0
        };
    }, [deviceStatistics, onlineStatus, deviceStatus]);

    const filterLabel = React.useMemo(() => {
        if (deviceStatus || onlineStatus) {
            return `${capitalize(onlineStatus || '')}${
                onlineStatus && deviceStatus ? ' & ' : ''
            }${capitalize(deviceStatus || '')}`;
        }

        return 'Device Status';
    }, [onlineStatus, deviceStatus]);

    // Publish the two status params as removable chips (labels cleaned of the
    // underscore form, e.g. `setup_needed` -> "Setup Needed"). Removing toggles
    // that param off.
    const deviceChips = React.useMemo<FilterChip[]>(() => {
        const toLabel = (value: string) =>
            value
                .split('_')
                .map((word) => capitalize(word))
                .join(' ');
        const result: FilterChip[] = [];
        if (onlineStatus) {
            result.push({
                key: `online_status:${onlineStatus}`,
                label: toLabel(onlineStatus),
                onRemove: () => handleFilterSet('online_status', onlineStatus)
            });
        }
        if (deviceStatus) {
            result.push({
                key: `device_status:${deviceStatus}`,
                label: toLabel(deviceStatus),
                onRemove: () => handleFilterSet('device_status', deviceStatus)
            });
        }
        return result;
    }, [onlineStatus, deviceStatus, handleFilterSet]);
    usePublishFilterChips('device', deviceChips);

    // Both status params share one pill group; the value decides which param it
    // toggles. Mutually-exclusive / empty options render as disabled pills.
    const statusOptions = React.useMemo(
        () => [
            {
                value: 'online',
                label: filterLabels.onlineLabel,
                active: onlineStatus === 'online',
                disabled: isDisabledStatus.online
            },
            {
                value: 'offline',
                label: filterLabels.offlineLabel,
                active: onlineStatus === 'offline',
                disabled: isDisabledStatus.offline
            },
            {
                value: 'ordered',
                label: filterLabels.orderedLabel,
                active: deviceStatus === 'ordered',
                disabled: isDisabledStatus.ordered
            },
            {
                value: 'delivered',
                label: filterLabels.deliveredLabel,
                active: deviceStatus === 'delivered',
                disabled: isDisabledStatus.delivered
            },
            {
                value: 'setup_needed',
                label: filterLabels.setupNeededLabel,
                active: deviceStatus === 'setup_needed',
                disabled: isDisabledStatus.setupNeeded
            }
        ],
        [filterLabels, onlineStatus, deviceStatus, isDisabledStatus]
    );

    const handleToggle = React.useCallback(
        (value: string) => {
            if (value === 'online' || value === 'offline') {
                handleOnlineStatusChange(value);
            } else {
                handleDeviceStatusChange(value);
            }
        },
        [handleOnlineStatusChange, handleDeviceStatusChange]
    );

    return (
        <Filter
            filterLabel={filterLabel}
            width="28rem"
            inline={inline}
            heading="Device Status"
            hasFilters={!!(onlineStatus || deviceStatus)}
            count={(onlineStatus ? 1 : 0) + (deviceStatus ? 1 : 0)}
            onShowAll={handleReset}
        >
            <FilterPillGroup options={statusOptions} onToggle={handleToggle} />
        </Filter>
    );
};

export default DeviceFilter;
